// User-friendly messages for idempotency-specific error codes (TD-126).
//
// The backend returns these error codes when idempotency constraints
// are violated. This module provides human-readable messages that can
// be shown to users without exposing internal implementation details.

/**
 * Known idempotency error codes returned by the backend.
 */
export const IDEMPOTENCY_ERROR_CODES = {
  /** 400 — request is missing the required Idempotency-Key header. */
  REQUIRED: "IDEMPOTENCY_KEY_REQUIRED",
  /** 422 — same key reused with a different request body. */
  PAYLOAD_MISMATCH: "IDEMPOTENCY_KEY_PAYLOAD_MISMATCH",
  /** 409 — concurrent request with the same key is still in-flight. */
  CONCURRENT_COMMIT: "IDEMPOTENCY_KEY_CONCURRENT_COMMIT",
} as const;

type IdempotencyErrorCode =
  (typeof IDEMPOTENCY_ERROR_CODES)[keyof typeof IDEMPOTENCY_ERROR_CODES];

/** User-facing messages keyed by idempotency error code. */
const MESSAGES: Record<IdempotencyErrorCode, string> = {
  [IDEMPOTENCY_ERROR_CODES.REQUIRED]:
    "Please retry your request.",
  [IDEMPOTENCY_ERROR_CODES.PAYLOAD_MISMATCH]:
    "Your request changed between attempts. Please refresh and try again.",
  [IDEMPOTENCY_ERROR_CODES.CONCURRENT_COMMIT]:
    "Request already processed. Please refresh to see the result.",
};

/**
 * Returns a user-friendly message for an idempotency error code,
 * or undefined if the code is not an idempotency error.
 *
 * Usage:
 * ```ts
 * catch (err) {
 *   if (err instanceof ApiError) {
 *     const msg = idempotencyUserMessage(err.code);
 *     if (msg) { showToast(msg); return; }
 *   }
 * }
 * ```
 */
export function idempotencyUserMessage(code: string): string | undefined {
  return MESSAGES[code as IdempotencyErrorCode];
}

/**
 * Returns true if the error code is an idempotency-specific code.
 * Useful for log-level decisions — REQUIRED is a code bug (warn),
 * PAYLOAD_MISMATCH and CONCURRENT_COMMIT are user-facing (info).
 */
export function isIdempotencyError(code: string): boolean {
  return Object.values(IDEMPOTENCY_ERROR_CODES).includes(
    code as IdempotencyErrorCode,
  );
}

/**
 * Returns true if the code indicates a missing header — which means
 * a code path failed to attach the Idempotency-Key. This should be
 * logged as a warning since it indicates a SPA bug, not a user error.
 */
export function isIdempotencyCodeBug(code: string): boolean {
  return code === IDEMPOTENCY_ERROR_CODES.REQUIRED;
}

/**
 * Generates a fresh Idempotency-Key header object for a mutation request.
 * Called per-request — never cached. (TD-126)
 *
 * Lives here, beside the errors it prevents, because every `regIdem`/`bearerIdem`
 * route needs it and a missing key fails with IDEMPOTENCY_KEY_REQUIRED (rule 79;
 * TD-449 is what happened when one call site forgot). It was previously a
 * module-private copy in cart-context.tsx, so each new call site hand-inlined
 * `crypto.randomUUID()` and there was nowhere to put the fallback below.
 */
export function idempotencyHeaders(): Record<string, string> {
  return { "Idempotency-Key": newIdempotencyKey() };
}

/**
 * `crypto.randomUUID` is only defined in SECURE contexts, so an embed mounted on
 * a plain-http page (a staging site, a customer previewing before TLS) throws
 * `TypeError: crypto.randomUUID is not a function` — which the caller's catch
 * then reports to the customer as a generic "failed" message that names nothing.
 * Degrade instead: an idempotency key needs to be UNIQUE, not unguessable, so
 * getRandomValues and finally Math.random are both adequate substitutes.
 */
function newIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  // RFC 4122 v4 version/variant bits, so the value is a well-formed UUID
  // whichever branch produced the bytes.
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
